/**
 * Student theme — l'arène, en clair (Brand core V16).
 *
 * Ce que l'élève porte en propre, par-dessus le socle marine partagé :
 *  - `role` = son orange d'AFFICHAGE (`#EA6421` : anneaux, segments, puces, glyphes du jeu), et la
 *    tinte de sa chip de gain à cet orange : le corail d'univers `#EE5F49`, resté celui des autres
 *    univers, faisait une seconde famille à côté du bouton orange (proposé le 15/10, à valider) ;
 *  - son BOUTON : un aplat ORANGE sur son ton foncé, libellé blanc, `#C55016` / `#993B0D`
 *    (décision de Wajih, 15/10 : le corail du kit, `#D23F28`, était « un peu trop rouge ») —
 *    `<Button>` le lit par défaut ;
 *  - son orange d'ÉCRITURE (`#BC4C15`) : le libellé du secondaire et du tertiaire, l'option
 *    non choisie, l'onglet ouvert ;
 *  - son option CHOISIE à l'orange du bouton, pas à celui d'affichage : le blanc n'y tient que
 *    3,1:1, il tient 4,6:1 sur le bouton (décision de Wajih, 13/10) ;
 *  - ce qui DÉTRUIT à l'aplat foncé du rouge, sur un relief plus sombre encore : le rouge plein
 *    et le bouton corail ne s'y distinguaient pas (ΔE 8,3 ; décision de Wajih, 13/10). Le rouge
 *    d'un message, d'un bandeau, d'une chip ne bouge pas ;
 *  - les fonds du livre, les mêmes que ceux du parent : « La page est #F5F6F8, les cartes sont
 *    blanches » (chap. 03), et la lavande pour les encarts.
 *
 * Les titres, le texte et les icônes restent marine (« Marine, la base commune ») : `primary`
 * n'est pas repointé. Le versant sombre est `studentDark.ts`.
 */
import { baseTheme } from "./base"
import { brand } from "./tokens/colors"

export const studentTheme = {
    ...baseTheme,
    colors: {
        ...baseTheme.colors,
        role: brand.roleStudentDisplay,
        "role-student-tint": brand.roleStudentDisplayTint,
        "role-strong": brand.roleStudentInk,
        "role-fill": brand.roleStudentFill,
        "relief-role": brand.roleStudentShade,
        "text-on-role": brand.white,
        "text-on-secondary": brand.roleStudentInk,
        "text-link": brand.roleStudentInk,
        "role-selected": brand.roleStudentFill,
        "danger-fill": brand.errorInk,
        "relief-danger": brand.errorInkShade,
        surface: brand.page,
        "surface-subtle": brand.lavender,
        // Un statut qui s'écrit prend son encre, pas son aplat : le rouge plein ne tenait que 4,0 sur
        // une carte (3,4 sur sa tinte), l'orange d'alerte 2,4, le vert 3,4. Le teal et le violet des
        // axes du score tiennent l'AA en clair : ils restent l'aplat.
        "text-success": brand.progressInk,
        "text-warning": brand.alertInk,
        "text-danger": brand.errorInk,
        // La lueur d'accueil à l'orange de l'élève, pas au jaune (Wajih, 18/10) : son espace ne se
        // confond pas avec celui du parent. Très adoucie par la recette de `<Glow>` (16 % au coin,
        // 7,2 % au palier, rien au tiers) ; sur la nuit, elle n'est pas montée.
        glow: brand.roleStudentDisplay,
        // Le voile de ses feuilles et de ses boîtes, plus sombre que celui du parent (Wajih, 20/10) : la
        // feuille blanche tient 7,1:1 contre lui au lieu de 4,3 (`contrast.test.ts`).
        scrim: brand.scrimDeep,
    },
} as const
